import { useEffect, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { shake } from '../../ui/motion';
import { platform } from '../../platform';
import type { CircleFormState } from './circle/CircleFieldInput';
import type { StudioForm } from './studio-api';
import { StudioSheet } from './StudioSheet';
import { StudioUploadRights } from './StudioUploadRights';

/** Что берём с картинки — строка со щитом по назначению референса (§1.10, таблица экранов). */
export const referenceCaps: Record<string, string> = {
  style: 'Берём стиль: цвет, свет, обработку. Саму картинку и лица с неё не переносим',
  scene: 'Берём сцену: фон, свет и подачу. Саму картинку и лица с неё не переносим',
  look: 'Берём образ: одежду, позу, фон и свет. В кадре будете вы, лица с картинки не переносим',
};
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
export const REFERENCE_MIN_PX = 256;
async function imageSize(file: File): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      const timer = setTimeout(() => reject(new Error('image_timeout')), 10000);
      image.onload = () => {
        clearTimeout(timer);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        clearTimeout(timer);
        reject(new Error('image_invalid'));
      };
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
export function fileSize(bytes: number) {
  return bytes >= 1024 * 1024
    ? uiFormat('{0} МБ', (bytes / 1024 / 1024).toFixed(1).replace('.', ','))
    : uiFormat('{0} КБ', Math.max(1, Math.round(bytes / 1024)));
}
/** Проверка картинки до загрузки (§1.10): JPG, PNG или WebP до 10 МБ, от 256 px по короткой стороне; HEIC не принимается. */
export async function referenceProblem(file: File, field: StudioForm['fields'][number]): Promise<string> {
  const accept = field.upload?.accept?.length ? field.upload.accept : ACCEPT;
  const maximum = field.upload?.max_bytes ?? 10 * 1024 * 1024;
  if (!accept.includes(file.type)) return 'Картинка не подошла: не открывается, попробуйте JPG или PNG';
  if (file.size > maximum) return uiFormat('Картинка не подошла: больше {0} МБ', maximum / 1024 / 1024);
  try {
    const size = await imageSize(file);
    if (Math.min(size.width, size.height) < REFERENCE_MIN_PX)
      return uiFormat('Картинка не подошла: слишком маленькая, нужна от {0} px', REFERENCE_MIN_PX);
  } catch {
    return 'Картинка не подошла: не открывается, попробуйте JPG или PNG';
  }
  return '';
}

/** «Свой референс» (§1.10, BotRef): первая карточка ленты, выбор файла без листа, проверка, плашка под лентой.
    Референс заменяет стиль: при выборе style = 'none', «Убрать» возвращает стиль, что был до референса. */
export function useStudioReference({
  state,
  field,
  fallbackStyle,
  previewUrl,
}: {
  state: CircleFormState;
  field: StudioForm['fields'][number] | undefined;
  fallbackStyle?: string;
  previewUrl?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const card = useRef<HTMLButtonElement | null>(null);
  const prior = useRef<string | undefined>(undefined);
  const generation = useRef(0);
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState('');
  const [consent, setConsent] = useState<File | null>(null);
  const [meta, setMeta] = useState<{ name: string; size: number; url: string } | null>(null);
  useEffect(() => () => void (generation.current += 1), []);
  useEffect(() => () => (meta ? URL.revokeObjectURL(meta.url) : undefined), [meta]);
  const key = field?.key ?? 'reference';
  const uploaded = !!state.uploadIds?.[key] || !!state.inputs[key];
  const active = !!state.inputs[key];
  const say = (text: string) => {
    shake(card.current);
    platform.haptic('error');
    setProblem(text);
  };
  useEffect(() => {
    if (!problem) return;
    const timer = window.setTimeout(() => setProblem(''), 4000);
    return () => clearTimeout(timer);
  }, [problem]);
  async function take(file: File) {
    if (!field) return;
    if (!state.rights) {
      setConsent(file);
      return;
    }
    const current = ++generation.current;
    setProblem('');
    setChecking(true);
    try {
      const reason = await referenceProblem(file, field);
      if (generation.current !== current) return;
      if (reason) return say(reason);
      prior.current = state.inputs.style && state.inputs.style !== 'none' ? state.inputs.style : prior.current;
      state.setInputs(values => ({ ...values, style: 'none' }));
      setMeta({ name: file.name, size: file.size, url: URL.createObjectURL(file) });
      if (state.uploadIds?.[key]) state.clearUpload(key);
      const ok = await state.upload(key, file);
      if (generation.current !== current) return;
      if (!ok) say('Картинка не подошла: на ней защищённое или запрещённое содержимое');
    } finally {
      if (generation.current === current) setChecking(false);
    }
  }
  /** Нажатие карточки: пусто — выбор файла; картинка уже загружена, но выбран стиль — вернуть её без новой загрузки. */
  function press(element?: HTMLButtonElement | null) {
    if (element) card.current = element;
    if (!field || state.locked || checking) return;
    if (!uploaded) {
      input.current?.click();
      return;
    }
    if (!active) {
      prior.current = state.inputs.style && state.inputs.style !== 'none' ? state.inputs.style : prior.current;
      state.setInputs(values => ({ ...values, style: 'none' }));
      void state.upload(key, null);
    }
  }
  function remove() {
    if (state.locked) return;
    generation.current += 1;
    setChecking(false);
    state.clearUpload(key);
    setMeta(null);
    const back = prior.current ?? fallbackStyle;
    if (back) state.setInputs(values => ({ ...values, style: back }));
  }
  const hidden = field ? (
    <input
      ref={input}
      type="file"
      hidden
      aria-label={uiText('Загрузить картинку-референс')}
      accept={(field.upload?.accept?.length ? field.upload.accept : ACCEPT).join(',')}
      onChange={event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) void take(file);
      }}
    />
  ) : null;
  const consentSheet = consent ? (
    <StudioSheet title="Права на материалы" onClose={() => setConsent(null)}>
      <h2>{uiText('Права на материалы')}</h2>
      <StudioUploadRights state={state} />
      <button
        type="button"
        className="btn"
        disabled={state.locked || !state.rights}
        onClick={() => {
          const file = consent;
          setConsent(null);
          void take(file);
        }}
      >
        {uiText('Продолжить загрузку')}
      </button>
    </StudioSheet>
  ) : null;
  const url = meta?.url ?? previewUrl;
  return { field, key, uploaded, active, checking, problem, url, meta, press, remove, replace: () => input.current?.click(), hidden, consentSheet };
}
export type StudioReferenceController = ReturnType<typeof useStudioReference>;

